import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit, Upload, Save, X, Image as ImageIcon, FileText, CheckCircle2, XCircle, ExternalLink, Package } from "lucide-react";
import { toast } from "sonner";
import { api, API, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const empty = () => ({
  name: "", category: "Windows Software / EXE Tool", short_desc: "", full_desc: "",
  regular_price: 0, offer_price: null, currency: "INR",
  demo_video_url: "", tutorial_video_url: "", installation_guide: "",
  version: "", file_size_mb: null, system_requirement: "",
  active: true, featured: false, is_new: false,
});

const AdminStorePage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    try { setMe(JSON.parse(localStorage.getItem("ap_admin_user") || "null")); } catch {}
    load();
    api.get("/store/categories").then((r) => setCategories(r.data || []));
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([api.get("/admin/store/products"), api.get("/admin/store/orders")]);
      setProducts(p.data || []); setOrders(o.data || []);
    } catch (e) { if (e?.response?.status === 401) navigate("/admin/login"); else toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const isSuper = me?.role === "super_admin";

  const save = async () => {
    if (!editing.name?.trim() || !editing.category) { toast.error("Name & category required"); return; }
    setSaving(true);
    try {
      let saved;
      if (editing.id) {
        const { id, key, thumbnail_file, screenshot_files, sample_files, product_file, created_at, updated_at, _thumb, _screens, _samples, _product, ...body } = editing;
        saved = (await api.patch(`/admin/store/products/${editing.id}`, body)).data;
      } else {
        const { _thumb, _screens, _samples, _product, ...body } = editing;
        saved = (await api.post("/admin/store/products", body)).data;
      }
      // Uploads
      if (editing._thumb instanceof File) {
        const fd = new FormData(); fd.append("file", editing._thumb);
        await api.post(`/admin/store/products/${saved.id}/thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (Array.isArray(editing._screens) && editing._screens.length) {
        const fd = new FormData(); editing._screens.forEach((f) => fd.append("files", f));
        await api.post(`/admin/store/products/${saved.id}/screenshots`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (Array.isArray(editing._samples) && editing._samples.length) {
        const fd = new FormData(); editing._samples.forEach((f) => fd.append("files", f));
        await api.post(`/admin/store/products/${saved.id}/sample`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (editing._product instanceof File) {
        const fd = new FormData(); fd.append("file", editing._product);
        await api.post(`/admin/store/products/${saved.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing.id ? "Product updated" : "Product added");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/admin/store/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const markPaid = async (o) => {
    if (!window.confirm(`Mark order ${o.order_no} as PAID? Buyer will get instant download.`)) return;
    try { await api.post(`/admin/store/orders/${o.id}/mark-paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const rejectOrder = async (o) => {
    if (!window.confirm(`Reject order ${o.order_no}?`)) return;
    try { await api.post(`/admin/store/orders/${o.id}/reject`); toast.success("Rejected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-store-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-2"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900">AP Digital Store</h1>
          <p className="text-sm text-slate-500 mt-1">Manage products, upload files, and verify buyer payments.</p>
        </div>
        {isSuper && <Button onClick={() => setEditing(empty())} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2" /> New Product</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
            products.length === 0 ? <div className="rounded-lg border-2 border-dashed border-slate-200 p-10 text-center text-slate-500 bg-white/50 mt-3"><Package className="w-8 h-8 mx-auto mb-2" /> No products yet. Click "New Product".</div> :
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {products.map((p) => (
                <div key={p.id} className="rounded-lg bg-white border border-slate-200 overflow-hidden" data-testid={`prod-card-${p.key}`}>
                  <div className="h-32 bg-slate-100">
                    {p.thumbnail_file ? <img src={`${API}/store/products/${p.key}/thumbnail`} className="w-full h-full object-cover" alt="" /> :
                      <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon className="w-6 h-6" /></div>}
                  </div>
                  <div className="p-3">
                    <div className="text-xs text-slate-500">{p.category}</div>
                    <div className="font-semibold text-slate-900 text-sm truncate">{p.name}</div>
                    <div className="mt-1 flex gap-1 flex-wrap text-xs">
                      {p.featured && <Badge className="bg-orange-100 text-orange-800">Featured</Badge>}
                      {p.is_new && <Badge className="bg-green-100 text-green-800">New</Badge>}
                      {p.active === false && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
                      {p.product_file && <Badge className="bg-teal-100 text-teal-800">File ✓</Badge>}
                    </div>
                    <div className="mt-2 text-sm"><b>₹{p.offer_price || p.regular_price}</b>{p.offer_price && <span className="text-xs line-through text-slate-400 ml-1">₹{p.regular_price}</span>}</div>
                    {isSuper && (
                      <div className="mt-2 pt-2 border-t flex gap-2 text-xs">
                        <button onClick={() => setEditing(p)} className="text-teal-700 hover:underline" data-testid={`edit-prod-${p.key}`}><Edit className="w-3 h-3 inline" /> Edit</button>
                        <button onClick={() => del(p)} className="text-red-600 hover:underline ml-auto" data-testid={`del-prod-${p.key}`}><Trash2 className="w-3 h-3 inline" /> Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          }
        </TabsContent>

        <TabsContent value="orders">
          <div className="rounded-lg bg-white border border-slate-200 overflow-hidden mt-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Buyer</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Proof</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No orders yet.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100" data-testid={`order-${o.order_no}`}>
                    <td className="px-4 py-3 font-medium">{o.order_no}<div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</div></td>
                    <td className="px-4 py-3">{o.product_name}</td>
                    <td className="px-4 py-3">{o.buyer_name}<div className="text-xs text-slate-500">{o.buyer_mobile}</div></td>
                    <td className="px-4 py-3">₹{o.amount}</td>
                    <td className="px-4 py-3">
                      <Badge className={o.status === "paid" ? "bg-green-100 text-green-800" : o.status === "rejected" ? "bg-red-100 text-red-800" : o.status === "payment_uploaded" ? "bg-yellow-100 text-yellow-800" : "bg-slate-100 text-slate-800"}>{o.status}</Badge>
                      {o.download_count > 0 && <div className="text-xs text-slate-500 mt-1">DL: {o.download_count}x</div>}
                    </td>
                    <td className="px-4 py-3">
                      {o.proof_file ? <a href={`${API}/admin/store/orders/${o.id}/proof?auth=${encodeURIComponent(loadAuthToken() || "")}`} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline text-xs"><FileText className="w-3 h-3 inline" /> View</a> : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {o.status !== "paid" && o.status !== "rejected" && (
                        <>
                          <Button size="sm" onClick={() => markPaid(o)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`mark-paid-${o.order_no}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid</Button>
                          <Button size="sm" variant="outline" onClick={() => rejectOrder(o)} className="ml-1 text-xs h-7"><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ProductEditor prod={editing} categories={categories} onChange={setEditing} onClose={() => setEditing(null)} onSave={save} saving={saving} />
    </div>
  );
};

const ProductEditor = ({ prod, categories, onChange, onClose, onSave, saving }) => {
  const [thumbPreview, setThumbPreview] = useState(null);
  useEffect(() => {
    if (!prod) return;
    if (prod._thumb instanceof File) { const u = URL.createObjectURL(prod._thumb); setThumbPreview(u); return () => URL.revokeObjectURL(u); }
    if (prod.thumbnail_file && prod.key) setThumbPreview(`${API}/store/products/${prod.key}/thumbnail`);
    else setThumbPreview(null);
  }, [prod]);
  if (!prod) return null;
  const set = (k, v) => onChange({ ...prod, [k]: v });

  return (
    <Dialog open={!!prod} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{prod.id ? `Edit — ${prod.name}` : "New Product"}</DialogTitle></DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Thumbnail */}
          <div>
            <Label className="font-semibold">Thumbnail</Label>
            <div className="mt-2 flex items-start gap-3">
              <div className="w-40 h-28 rounded-md bg-slate-100 border overflow-hidden flex items-center justify-center">
                {thumbPreview ? <img src={thumbPreview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-400" />}
              </div>
              <div>
                <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => set("_thumb", e.target.files?.[0])} data-testid="prod-thumb" className="text-sm" />
                <p className="text-xs text-slate-500 mt-1">JPG/PNG/WEBP, max 8MB</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Product Name *</Label><Input value={prod.name} onChange={(e) => set("name", e.target.value)} data-testid="prod-name" /></div>
            <div>
              <Label>Category *</Label>
              <Select value={prod.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Version</Label><Input value={prod.version || ""} onChange={(e) => set("version", e.target.value)} placeholder="1.0" /></div>
            <div><Label>Regular Price (₹)</Label><Input type="number" value={prod.regular_price || 0} onChange={(e) => set("regular_price", parseFloat(e.target.value) || 0)} /></div>
            <div><Label>Offer Price (₹, optional)</Label><Input type="number" value={prod.offer_price || ""} onChange={(e) => set("offer_price", parseFloat(e.target.value) || null)} /></div>
            <div><Label>File Size (MB)</Label><Input type="number" value={prod.file_size_mb || ""} onChange={(e) => set("file_size_mb", parseFloat(e.target.value) || null)} /></div>
            <div><Label>System Requirement</Label><Input value={prod.system_requirement || ""} onChange={(e) => set("system_requirement", e.target.value)} placeholder="Windows 10, 2GB RAM" /></div>
            <div className="col-span-2"><Label>Short Description</Label><Textarea rows={2} value={prod.short_desc || ""} onChange={(e) => set("short_desc", e.target.value)} placeholder="One-line pitch" /></div>
            <div className="col-span-2"><Label>Full Description</Label><Textarea rows={4} value={prod.full_desc || ""} onChange={(e) => set("full_desc", e.target.value)} placeholder="Detailed features, what's inside…" /></div>
            <div className="col-span-2"><Label>Demo Video URL (YouTube or MP4)</Label><Input value={prod.demo_video_url || ""} onChange={(e) => set("demo_video_url", e.target.value)} placeholder="https://youtu.be/…" /></div>
            <div className="col-span-2"><Label>Tutorial / Installation Video URL</Label><Input value={prod.tutorial_video_url || ""} onChange={(e) => set("tutorial_video_url", e.target.value)} placeholder="https://youtu.be/…" /></div>
            <div className="col-span-2"><Label>Installation Guide (step-by-step)</Label><Textarea rows={5} value={prod.installation_guide || ""} onChange={(e) => set("installation_guide", e.target.value)} placeholder="Step 1 – Download the file&#10;Step 2 – Extract ZIP&#10;Step 3 – Run setup.exe…" /></div>
            <div className="col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!prod.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!prod.highlight} onChange={(e) => set("highlight", e.target.checked)} data-testid="prod-highlight" /> ⭐ Highlight (Homepage pe bhi dikhayega)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!prod.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="prod-featured" /> Featured in Store</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!prod.is_new} onChange={(e) => set("is_new", e.target.checked)} /> New Badge</label>
            </div>
          </div>

          {/* File uploads */}
          <div className="rounded-md border border-slate-200 p-3 space-y-3 bg-slate-50/40">
            <div className="font-semibold text-sm">File Uploads</div>
            <div>
              <Label className="text-xs">Screenshots (multi)</Label>
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif" multiple onChange={(e) => set("_screens", Array.from(e.target.files || []))} className="text-sm block" data-testid="prod-screens" />
              {prod.screenshot_files?.length > 0 && <div className="text-xs text-slate-500 mt-1">{prod.screenshot_files.length} existing screenshot(s)</div>}
              {prod._screens?.length > 0 && <div className="text-xs text-teal-700 mt-1">{prod._screens.length} new to upload</div>}
            </div>
            <div>
              <Label className="text-xs">Sample / Preview files (images / small PDFs / demo)</Label>
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.mp4" multiple onChange={(e) => set("_samples", Array.from(e.target.files || []))} className="text-sm block" />
              {prod.sample_files?.length > 0 && <div className="text-xs text-slate-500 mt-1">{prod.sample_files.length} existing sample(s)</div>}
              {prod._samples?.length > 0 && <div className="text-xs text-teal-700 mt-1">{prod._samples.length} new to upload</div>}
            </div>
            <div>
              <Label className="text-xs">🔒 Main Product File (paid download — max 200MB)</Label>
              <input type="file" onChange={(e) => set("_product", e.target.files?.[0])} className="text-sm block" data-testid="prod-file" />
              {prod.product_file && <div className="text-xs text-slate-600 mt-1">Current: {prod.product_file.original_filename} ({(prod.product_file.size / 1024 / 1024).toFixed(1)} MB)</div>}
              {prod._product && <div className="text-xs text-teal-700 mt-1">New: {prod._product.name} — will replace on save</div>}
              <p className="text-xs text-orange-600 mt-1">⚠️ This file is only delivered to buyers who have paid.</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-prod-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Product"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminStorePage;
